//Frontend/src/unicodex_ui/pages/ManufacturerBatchesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManufacturerNav from "../components/ManufacturerNav";
import portalApi from "../services/portalApi";
import "../styles/ManufacturerPortal.css";

function ManufacturerBatchesPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);

  const [productId, setProductId] = useState("");
  const [ucxBatch, setUcxBatch] = useState("");
  const [batchSize, setBatchSize] = useState("");
  const [manufactureDate, setManufactureDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  function signOut() {
    localStorage.removeItem("ucx_mfg_token");
    localStorage.removeItem("ucx_mfg_user");
    navigate("/portal/login", { replace: true });
  }

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(productId)) || null,
    [products, productId]
  );

  async function loadProducts() {
    const pRes = await portalApi.listProducts();
    if (!pRes?.ok) throw new Error("products_failed");
    const ps = Array.isArray(pRes.products) ? pRes.products : [];
    setProducts(ps);
    if (!productId && ps[0]) setProductId(String(ps[0].id));
    return ps;
  }

  async function loadBatches(pid) {
    const params = {};
    if (pid) params.productId = Number(pid);

    const bRes = await portalApi.listBatches(params);
    if (!bRes?.ok) throw new Error("batches_failed");
    setBatches(Array.isArray(bRes.batches) ? bRes.batches : []);
  }

  async function fullLoad() {
    setLoading(true);
    setError("");
    try {
      const ps = await loadProducts();
      const effectivePid = productId || (ps[0] ? String(ps[0].id) : "");
      await loadBatches(effectivePid);
    } catch (e) {
      console.error("[MfgBatches] load error:", e);
      setError("Unable to load batches/products.");
      setProducts([]);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fullLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!productId) return;
    loadBatches(productId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function createBatch(e) {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        productId: Number(productId),
        ucxBatch: ucxBatch || null,
        batchSize: Number(batchSize),
        manufactureDate,
        expiryDate: expiryDate || null,
      };

      const res = await portalApi.createBatch(payload);
      if (!res?.ok) throw new Error("create_failed");

      setUcxBatch("");
      setBatchSize("");
      setManufactureDate("");
      setExpiryDate("");

      await loadBatches(productId);
    } catch (e) {
      console.error("[MfgBatches] create error:", e);
      setError("Unable to create batch. Check your fields.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="ucx-section ucx-mfg-portal-page">
      <div className="ucx-container">
        <ManufacturerNav title="Manufacturer Portal" subtitle="Batches" onSignOut={signOut} />

        <div className="ucx-mfg-panel">
          <div className="ucx-mfg-panel-head">
            <div>
              <h1 className="ucx-mfg-title">Batches</h1>
              <p className="ucx-mfg-subtitle">Create production batches for your products.</p>
            </div>

            <div className="ucx-mfg-actions">
              <select className="ucx-mfg-select" value={productId} onChange={(e) => setProductId(e.target.value)}>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.ucx_sku || "—"})
                  </option>
                ))}
              </select>

              <button type="button" className="ucx-btn ucx-btn-outline" onClick={() => loadBatches(productId)} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>

          {error && <div className="ucx-mfg-error">{error}</div>}

          <form className="ucx-mfg-form" onSubmit={createBatch}>
            <div className="ucx-mfg-form-row">
              <div className="ucx-mfg-field">
                <label>Product</label>
                <input value={selectedProduct ? `${selectedProduct.name} (${selectedProduct.ucx_sku || "—"})` : ""} disabled />
              </div>

              <div className="ucx-mfg-field">
                <label>UCX Batch (optional)</label>
                <input value={ucxBatch} onChange={(e) => setUcxBatch(e.target.value)} placeholder="e.g. LOT-2025-001" />
              </div>

              <div className="ucx-mfg-field">
                <label>Batch size</label>
                <input value={batchSize} onChange={(e) => setBatchSize(e.target.value.replace(/[^\d]/g, ""))} required />
              </div>

              <div className="ucx-mfg-field">
                <label>Manufacture date</label>
                <input type="date" value={manufactureDate} onChange={(e) => setManufactureDate(e.target.value)} required />
              </div>

              <div className="ucx-mfg-field">
                <label>Expiry date (optional)</label>
                <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
              </div>

              <button type="submit" className="ucx-btn ucx-btn-primary" disabled={saving || !productId}>
                {saving ? "Saving…" : "Create batch"}
              </button>
            </div>
          </form>

          <div className="ucx-mfg-tablewrap">
            <table className="ucx-mfg-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product</th>
                  <th>UCX Batch</th>
                  <th>Size</th>
                  <th>MFG</th>
                  <th>EXP</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="ucx-mfg-empty">Loading…</td>
                  </tr>
                )}

                {!loading && batches.length === 0 && (
                  <tr>
                    <td colSpan={7} className="ucx-mfg-empty">No batches found.</td>
                  </tr>
                )}

                {batches.map((b) => (
                  <tr key={b.id}>
                    <td>{b.id}</td>
                    <td>{b.product_name || b.product_id}</td>
                    <td>{b.ucx_batch || "-"}</td>
                    <td>{b.batch_size}</td>
                    <td>{b.manufacture_date ? String(b.manufacture_date).slice(0, 10) : "-"}</td>
                    <td>{b.expiry_date ? String(b.expiry_date).slice(0, 10) : "-"}</td>
                    <td>{b.status || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ucx-mfg-note">
            Next: go to <b>Codes</b> to generate and view codes per batch.
          </div>
        </div>
      </div>
    </section>
  );
}

export default ManufacturerBatchesPage;
